import useFlag from 'hooks/useFlag'
import useModifiers from 'hooks/useModifiers'
import useProducts from 'hooks/useProducts'
import useStableStringArray from 'hooks/useStableStringArray'
import { Fields as Modifier } from 'models/Modifier'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { errorHandler } from 'utilities/errorHandler'

export function useModifierSelectData(
  selectedIds: string[],
  productIds: string[],
  setValue: (val: string[]) => void
) {
  const postLookupEnabled = useFlag('mx-modifier-options-post', false)
  const [searchTerm, setSearchTerm] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [page, setPage] = useState(1)
  const [selectedModifiers, setSelectedModifiers] = useState<Modifier[]>([])

  const stableProductIds = useStableStringArray(productIds)

  // Reset the pagination data when the search term or productIds change
  useEffect(() => setPage(1), [searchTerm, stableProductIds])
  // Reset the pagination data when the search term or productIds change
  useEffect(() => setSearchTerm(''), [stableProductIds])

  const isModifierSelectedBy = (idArray: string[]) => (modifier: Modifier) =>
    idArray.includes(modifier.option.chain_uid)

  const fetchModifierParams = useMemo(
    () => ({
      search_term: searchTerm,
      page,
      item_ids: productIds,
    }),
    [searchTerm, page, productIds]
  )

  const { modifiers, pagination, isReady, isLoading, error } = useModifiers(
    fetchModifierParams,
    {
      shouldSkip: !productIds.length,
      paginationKey: 'all',
    }
  )

  // Keep selected modifier items in sync with modifier items (e.g. after adding a product)
  useEffect(() => {
    setSelectedModifiers(prev =>
      prev.map(selectedModifier => {
        const modifier = modifiers.find(m => m.uid === selectedModifier.uid)

        if (!modifier) {
          return selectedModifier
        } else {
          return {
            ...selectedModifier,
            items: modifier.items,
          }
        }
      })
    )
  }, [modifiers])

  const deselectModifiers = useCallback(
    (modifiersList, idsList) => {
      const idsToDeselect = idsList.filter(id => {
        const modifier = modifiersList.find(
          modifier => modifier.option.chain_uid === id
        )
        if (!modifier) return false

        return !modifier.items.some(item => productIds.includes(item.chain_uid))
      })

      if (idsToDeselect.length) {
        const newSelectedIds = idsList.filter(id => !idsToDeselect.includes(id))
        setValue(newSelectedIds)

        // Update selectedModifiers by removing the deselected ones
        setSelectedModifiers(prev =>
          prev.filter(
            modifier => !idsToDeselect.includes(modifier.option.chain_uid)
          )
        )
      }
    },
    [productIds, setValue, setSelectedModifiers]
  )

  // Keep a ref to avoid re-triggering the deselect effect on every selection change
  const selectedModifiersRef = useRef(selectedModifiers)
  selectedModifiersRef.current = selectedModifiers

  // If the product associated with a modifier is removed, deselect the modifier
  useEffect(() => {
    if (!productIds.length && !selectedIds.length) return

    const currentSelected = selectedModifiersRef.current
    if (currentSelected.length)
      return deselectModifiers(currentSelected, selectedIds)
    if (selectedIds.length && modifiers.length && isReady)
      return deselectModifiers(modifiers, selectedIds)
    // selectedModifiersRef is intentionally read via ref to avoid re-triggering this effect on every selection change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productIds, selectedIds, modifiers, isReady, deselectModifiers])

  // Saved ids not yet in the modifier store — the by-id lookup below fetches
  // these. Not gated on the store being non-empty: a lookup failure wipes the
  // whole store, and the wiped ids must count as missing again to re-fetch.
  const missingSelectedIds = useMemo(() => {
    if (!selectedIds.length || !productIds.length || !isReady) return []
    const storeIds = new Set(
      modifiers.map(modifier => modifier.option.chain_uid)
    )
    return selectedIds.filter(id => !storeIds.has(id))
  }, [selectedIds, productIds, modifiers, isReady])

  const missingKey = useMemo(
    () => missingSelectedIds.join(','),
    [missingSelectedIds]
  )
  // Missing sets already retried after a failure. A failure wipes the store
  // and re-expands the missing set, so an alternating succeed-then-fail
  // lookup would refetch forever — one retry per set, then stop fetching.
  const retriedMissingKeysRef = useRef(new Set<string>())

  // Resolve missing ids over POST so they ride in the body, not an over-long
  // URL. Each lookup returns at most one page of the requested ids; the
  // linking effect below re-fires for the still-missing ids until they shrink
  // to zero.
  const {
    completedFetchCount: backfillFetchCount,
    activeFetchCount: backfillActiveFetchCount,
    error: backfillError,
  } = useModifiers(
    { option_ids: missingSelectedIds, item_ids: productIds },
    {
      shouldSkip:
        !missingSelectedIds.length ||
        retriedMissingKeysRef.current.has(missingKey),
      preventCancellation: true,
      paginationKey: 'selected',
      method: postLookupEnabled ? 'post' : 'get',
    }
  )

  // 4xx lookup failures reach neither the user nor Sentry (the global alert
  // middleware only covers 5xx) — report them, or a missing options route
  // degrades every large selection with zero operational signal.
  useEffect(() => {
    if (!backfillError) return
    errorHandler.capture('Saved modifier by-id lookup failed', {
      savedIdCount: selectedIds.length,
      error: backfillError.message,
    })
    // Report once per failure, not again on every selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backfillError])

  // Saved ids not yet linked into selectedModifiers.
  const unresolvedSelectedIds = useMemo(() => {
    const linkedIds = new Set(
      selectedModifiers.map(modifier => modifier.option.chain_uid)
    )
    return selectedIds.filter(id => !linkedIds.has(id))
  }, [selectedIds, selectedModifiers])

  // Give-up latch, keyed to the missing set: a completed lookup that makes no
  // progress settles that set — its leftover ids can't resolve. A different
  // saved selection arriving later re-enters the loading state.
  const [settledMissingKey, setSettledMissingKey] = useState<string | null>(
    null
  )
  const backfillProgressRef = useRef<{ count: number; key: string } | null>(
    null
  )

  useEffect(() => {
    const progress = backfillProgressRef.current
    if (!progress) {
      backfillProgressRef.current = {
        count: backfillFetchCount,
        key: missingKey,
      }
      return
    }
    // A lookup ran, nothing is in flight, and no ids are missing: the
    // selection resolved. (A wiped store puts its ids back into the missing
    // set, so an empty set here can't mean a wipe.)
    if (
      !missingSelectedIds.length &&
      backfillFetchCount > 0 &&
      !backfillActiveFetchCount
    ) {
      return setSettledMissingKey(missingKey)
    }
    // Judge only on observed completions, and only while nothing is in
    // flight — a store update can land a commit before its fetch's count
    // does, and an overlapping duplicate lookup completes with no store
    // change; either would misread as "no progress".
    if (backfillFetchCount === progress.count) {
      if (!backfillActiveFetchCount) {
        // An already-retried set with nothing in flight can never be fetched
        // again (the lookup skips it) — settle rather than strand.
        if (
          missingSelectedIds.length &&
          retriedMissingKeysRef.current.has(missingKey)
        ) {
          return setSettledMissingKey(missingKey)
        }
        // While idle, adopt key changes that no lookup caused (a browse page
        // landing, a product change) — the next completion must be judged
        // against the set it actually fetched, or a failure that changes
        // nothing reads as progress and the latch never settles.
        if (missingKey !== progress.key) {
          backfillProgressRef.current = {
            count: backfillFetchCount,
            key: missingKey,
          }
        }
      }
      return
    }
    // Progress only ever shrinks the missing set — growth means a wipe
    // re-expanded it and a retry is already in flight (launched on the wipe
    // render, before this completion was judged). Record the retried set;
    // the second wipe back to it settles instead of looping.
    const progressSize = progress.key ? progress.key.split(',').length : 0
    if (missingSelectedIds.length > progressSize) {
      if (retriedMissingKeysRef.current.has(missingKey)) {
        if (!backfillActiveFetchCount) return setSettledMissingKey(missingKey)
      } else {
        retriedMissingKeysRef.current.add(missingKey)
      }
    }
    if (backfillActiveFetchCount > 0) {
      backfillProgressRef.current = {
        count: backfillFetchCount,
        key: missingKey,
      }
      return
    }
    // A quiet completion that left the missing set unchanged: the leftover
    // ids can't resolve (errored, or no longer in the menu) — give up.
    if (missingKey === progress.key) return setSettledMissingKey(missingKey)
    backfillProgressRef.current = { count: backfillFetchCount, key: missingKey }
  }, [
    missingSelectedIds.length,
    missingKey,
    backfillFetchCount,
    backfillActiveFetchCount,
  ])

  // Resolving while any saved id is unlinked and we haven't given up on the
  // current missing set — the UI holds a loading state so the selection
  // renders once, not page by page. Without products nothing can resolve
  // (the ordering service requires item_ids), so never report resolving.
  const isResolvingSelection =
    productIds.length > 0 &&
    unresolvedSelectedIds.length > 0 &&
    missingKey !== settledMissingKey

  // If this component is instantiated with selectedIds that are not in the selectedModifiers list,
  // link the selectedModifiers to the selectedIds as modifiers become available.
  // Runs progressively as the ORM store grows (browse fetch, then option_ids fetch).
  useEffect(() => {
    if (!selectedIds.length || !modifiers.length || !isReady) return

    const currentSelected = selectedModifiersRef.current
    // Match on content and count, not size alone — an equal-sized swap must
    // relink, and a smaller selection must shed the stale extras.
    const currentSelectedIds = new Set(
      currentSelected.map(modifier => modifier.option.chain_uid)
    )
    const matchesSelection =
      currentSelected.length === selectedIds.length &&
      selectedIds.every(id => currentSelectedIds.has(id))
    if (matchesSelection) return

    const modifiersById = new Map(
      modifiers.map(modifier => [modifier.option.chain_uid, modifier])
    )
    const foundModifiers = selectedIds
      .map(id => modifiersById.get(id))
      .filter(Boolean) as Modifier[]

    // Replace once we've found at least as much as remains saved — comparing
    // against only the still-saved chips lets a smaller selection take over,
    // while a half-populated store can't shrink an in-progress link.
    const selectedIdSet = new Set(selectedIds)
    const stillSelectedCount = currentSelected.filter(modifier =>
      selectedIdSet.has(modifier.option.chain_uid)
    ).length
    if (foundModifiers.length && foundModifiers.length >= stillSelectedCount) {
      setSelectedModifiers(foundModifiers)
    }
    // selectedModifiersRef is intentionally read via ref to avoid re-triggering this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, modifiers, isReady])

  const { shouldDisplayIds: displayProductIdInput } = useProducts()
  // Giving up with lookup errors degrades to the raw id input, which shows
  // every saved id and survives editing — rendering the resolved subset as a
  // final selection invites edits that drop the rest.
  const backfillGaveUp =
    unresolvedSelectedIds.length > 0 &&
    missingKey === settledMissingKey &&
    !!backfillError
  const shouldDisplayIdInput =
    displayProductIdInput || (isReady && !!error) || backfillGaveUp

  return {
    modifiersData: {
      modifiers,
      pagination,
      isReady,
      isLoading,
      error,
    },
    searchState: {
      searchTerm,
      setSearchTerm,
      page,
      setPage,
      isFocused,
      setIsFocused,
    },
    selection: {
      selectedModifiers,
      setSelectedModifiers,
      isModifierSelectedBy,
      isResolvingSelection,
    },
    uiState: {
      shouldDisplayIdInput,
    },
  }
}
